import { useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ThemeProvider } from "@app/providers/ThemeProvider";
import { MobileMenuProvider, useMobileMenu } from "@app/providers/MobileMenuProvider";
import { Navbar } from "@components/index";
import { ErrorBoundary } from "@shared/components/ui/ErrorBoundary";
import { LoadingBoundary } from "@shared/components/ui/LoadingBoundary";
import { NotFound } from "@shared/components/ui/NotFound";
import { ROUTES } from "./app/config/routes";
import Footer from "@shared/components/layout/Footer";
import { lazy } from "react";
import FloatingActions from "@shared/components/FloatingActions";
import WhatsAppButton from "@shared/components/ui/WhatsAppButton";
import FloatingUserButton from "@shared/components/FloatingUserButton";


// Lazy load de todas las páginas
const HomePage = lazy(() => import("@features/home/pages/HomePage"));
const InfraestructuraPage = lazy(() => import("@features/infraestructura/pages/InfraestructuraPage"));
const ServiciosCloudPage = lazy(() => import("@features/servicios-cloud/pages/ServiciosCloudPage"));
const ServiciosTIPage = lazy(() => import("@features/servicios-ti/pages/ServiciosTIPage"));
const ProyectosPage = lazy(() => import("@features/proyectos/pages/ProyectosPage"));
const ServidoresPage = lazy(() => import("@features/servidoresdedicados/pages/servidoresPage"));
const QuienesSomosPage = lazy(() => import("@features/home/pages/QuienesSomosPage"));
const PoliticasPrivacidadPage = lazy(() => import("@features/home/pages/PoliticasPrivacidadPage"));
const ServidoresVPSPage = lazy(() => import("@features/servidores-vps/pages/ServidoresVPSPage"));
const ServidoresNubePage = lazy(() => import("@features/servidores-nube/pages/ServidoresNubePage"));
const Office365Page = lazy(() => import("./features/servicios-cloud/pages/Office365Page"));
const AcronisPage = lazy(() => import("./features/servicios-ti/pages/AcronisPage"));
const KasperskyPage = lazy(() => import("./features/servicios-ti/pages/KasperskyPage"));
const ContactPage = lazy(() => import("@features/contacto/page"));  

// Páginas de presentación de cada producto: /proyectos/<slug>
const ProductRoute = lazy(() => import("@features/productos/ProductRoute").then((m) => ({ default: m.ProductRoute })));
const CatalogRedirect = lazy(() => import("@features/productos/ProductRoute").then((m) => ({ default: m.CatalogRedirect })));
const ServiciosCatalogoPage = lazy(() => import("@features/proyectos/pages/ServiciosCatalogoPage"));

const AppContent = () => {
  const location = useLocation();
  const { isOpen: isMobileMenuOpen } = useMobileMenu();

  // Al cambiar de página el scroll vuelve arriba del todo (salvo enlaces con #ancla).
  useEffect(() => {
    if (location.hash) return;
    if ("scrollRestoration" in window.history) window.history.scrollRestoration = "manual";
    const toTop = () => window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    toTop();
    // Las páginas cargan en diferido: se repite cuando la nueva ya está pintada.
    const raf = requestAnimationFrame(() => requestAnimationFrame(toTop));
    const late = window.setTimeout(toTop, 350);
    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(late);
    };
  }, [location.pathname, location.hash]);

  useEffect(() => {
    document.documentElement.classList.add("tec-public-page");
    return () => {
      document.documentElement.classList.remove("tec-public-page");
    };
  }, []);

  const showTecFab =
    location.pathname === ROUTES.HOME || location.pathname === ROUTES.PROYECTOS;

  return (
    <div className="public-shell">
      <Navbar />
      <main className="public-main" role="main">
        <LoadingBoundary>
          <Routes>
            <Route path={ROUTES.HOME} element={<HomePage />} />
            {/* Desarrollo quedó dentro de Proyectos */}
            <Route path={ROUTES.DESARROLLO} element={<Navigate to={ROUTES.PROYECTOS} replace />} />
            <Route path={ROUTES.INFRAESTRUCTURA} element={<InfraestructuraPage />} />
            <Route path={ROUTES.SERVICIOS_CLOUD} element={<ServiciosCloudPage />} />
            <Route path={ROUTES.SERVICIOS_TI} element={<ServiciosTIPage />} />
            <Route path={ROUTES.PROYECTOS} element={<ProyectosPage />} />
            <Route path={ROUTES.SERVIDORES_DEDICADOS} element={<ServidoresPage />} />
            <Route path={ROUTES.CONTACTO} element={<ContactPage />} />
            <Route path={ROUTES.QUIENES_SOMOS} element={<QuienesSomosPage />} />
            <Route path={ROUTES.POLITICAS_PRIVACIDAD} element={<PoliticasPrivacidadPage />} />
            <Route path={ROUTES.VPS_COTIZAR} element={<ServidoresVPSPage />} />
            <Route path={ROUTES.NUBE_COTIZAR} element={<ServidoresNubePage />} />
            <Route path={ROUTES.OFFICE365_COTIZAR} element={<Office365Page />} />
            <Route path={ROUTES.ACRONIS_COTIZAR} element={<AcronisPage />} />
            <Route path={ROUTES.KASPERSKY} element={<KasperskyPage />} />
            
            {/* Presentación de cada producto (el catálogo PDF va dentro) */}
            <Route path={ROUTES.PROYECTO_SERVICIOS_CATALOGO} element={<ServiciosCatalogoPage />} />
            <Route path="/proyectos/:slug" element={<ProductRoute />} />
            <Route path="/proyectos/:slug/catalogo" element={<CatalogRedirect />} />

            <Route path="*" element={<NotFound />} />
          </Routes>
        </LoadingBoundary>
      </main>
      <Footer />
      {!isMobileMenuOpen && (
        <FloatingActions>
          {showTecFab && <FloatingUserButton />}
          <WhatsAppButton />
        </FloatingActions>
      )}
    </div>
  );
};

const App = () => {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <Router>
          <MobileMenuProvider>
            <AppContent />
          </MobileMenuProvider>
        </Router>
      </ThemeProvider>
    </ErrorBoundary>
  );
};

export default App;
